import { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";

export default function ReportAdminView() {
  const { proj, activeId, apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm, stitle, th, td } = useApp();
  if (!proj) return null;

  // ─── Client Cabinets state ───────────────────────────────────────
  const [cabs, setCabs] = useState([]);
  const [cabUrl, setCabUrl] = useState("");
  const [cabEmail, setCabEmail] = useState("");
  const [cabName, setCabName] = useState("");
  const [cabPos, setCabPos] = useState("");
  const [cabBusy, setCabBusy] = useState(false);
  const [lastPass, setLastPass] = useState(null); // {email, password} after creation
  const [copied, setCopied] = useState("");

  // ─── Налаштування звіту: блоки (вмик/вимк + порядок) + коментар + прев'ю ───
  const RP_BLOCK_LABELS = {
    value: "Заявки з органіки (GA4)",
    workplan: "План робіт", kanban: "Задачі (канбан)", seoTexts: "Тексти на погодження",
    tickets: "Запити та зауваження", kpi: "Ключові показники", cwv: "Технічний аудит (CWV)",
    backlinks: "Посилальний профіль", ga4: "Поведінка (GA4)", gscTraffic: "Динаміка кліків/показів",
    positions: "Динаміка позицій", keywordGroups: "Групи ключів + конкуренти", hfKeywords: "ВЧ (високочастотні) ключі", coverage: "Індексація",
    aiVis: "AI-видимість (ChatGPT/Perplexity)",
  };
  const RP_DEFAULT_ORDER = ["value", "workplan", "kanban", "seoTexts", "tickets", "kpi", "cwv", "backlinks", "ga4", "gscTraffic", "positions", "keywordGroups", "hfKeywords", "coverage", "aiVis"];
  const [blocks, setBlocks] = useState(() => RP_DEFAULT_ORDER.map(key => ({ key, enabled: true })));
  const [reportComment, setReportComment] = useState("");
  const [cfgSaving, setCfgSaving] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [wpMonths, setWpMonths] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(0);
  const [taskColumns, setTaskColumns] = useState([]); // назви колонок канбану у звіт
  const [colOptions, setColOptions] = useState([]);   // { title, color } проєкту

  const loadCfg = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/onboarding`);
      const d = await r.json();
      const saved = Array.isArray(d?.reportConfig?.blocks) ? d.reportConfig.blocks : null;
      if (saved) {
        // мерджимо зі стандартом: нові блоки додаємо в кінець, прибрані ключі ігноруємо
        const known = new Set(saved.map(b => b.key));
        const merged = saved.filter(b => RP_DEFAULT_ORDER.includes(b.key));
        RP_DEFAULT_ORDER.forEach(k => { if (!known.has(k)) merged.push({ key: k, enabled: true }); });
        setBlocks(merged);
      }
      if (typeof d?.reportComment === "string") setReportComment(d.reportComment);
      if (typeof d?.reportConfig?.currentMonth === "number") setCurrentMonth(d.reportConfig.currentMonth);
      if (Array.isArray(d?.reportConfig?.taskColumns)) setTaskColumns(d.reportConfig.taskColumns);
      try {
        const cr = await apiFetch(`/api/kanban/columns?projectId=${activeId}`);
        const cd = await cr.json();
        if (Array.isArray(cd)) {
          const seen = new Set(); const opts = [];
          cd.forEach(c => { if (c.title && !seen.has(c.title)) { seen.add(c.title); opts.push({ title: c.title, color: c.color }); } });
          setColOptions(opts);
        }
      } catch {}
      try {
        const wr = await apiFetch(`/api/projects/${activeId}/workplan`);
        const wd = await wr.json();
        const months = Array.isArray(wd?.months) ? wd.months : (Array.isArray(wd) ? wd : []);
        setWpMonths(months);
      } catch {}
    } catch {}
  };

  const toggleBlock = (key) => setBlocks(prev => prev.map(b => b.key === key ? { ...b, enabled: !b.enabled } : b));
  const toggleCol = (title) => setTaskColumns(prev => prev.includes(title) ? prev.filter(x => x !== title) : [...prev, title]);
  const moveBlock = (idx, dir) => setBlocks(prev => {
    const next = [...prev]; const j = idx + dir;
    if (j < 0 || j >= next.length) return prev;
    [next[idx], next[j]] = [next[j], next[idx]];
    return next;
  });

  const saveCfg = async () => {
    setCfgSaving(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/onboarding`, {
        method: "PUT", body: JSON.stringify({ reportConfig: { blocks, currentMonth: currentMonth || null, taskColumns }, reportComment }),
      });
      if (r.ok) flash("✓ Налаштування звіту збережено"); else flash("Помилка збереження");
    } catch (err) { flash("Помилка: " + err.message); }
    setCfgSaving(false);
  };

  const openPreview = async () => {
    setPreviewBusy(true);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/report-preview-token`);
      const d = await r.json();
      if (r.ok && d.token) window.open(`/cabinet/${activeId}?preview=${encodeURIComponent(d.token)}`, "_blank");
      else flash(d.error || "Не вдалось відкрити прев'ю");
    } catch (err) { flash("Помилка: " + err.message); }
    setPreviewBusy(false);
  };

  const loadCabs = async () => {
    try {
      const r = await apiFetch(`/api/projects/${activeId}/cabinets`);
      const d = await r.json();
      if (r.ok) { setCabs(d.cabinets || []); setCabUrl(d.cabinetUrl || ""); }
    } catch {}
  };

  useEffect(() => { loadCabs(); loadCfg(); }, [activeId]);

  const invite = async (e) => {
    e.preventDefault();
    if (!cabEmail) return;
    setCabBusy(true); setLastPass(null);
    try {
      const r = await apiFetch(`/api/projects/${activeId}/cabinets`, {
        method: "POST",
        body: JSON.stringify({ email: cabEmail, fullName: cabName, position: cabPos }),
      });
      const d = await r.json();
      if (r.ok) {
        setLastPass({ email: d.cabinet.email, password: d.password, url: d.cabinetUrl });
        setCabEmail(""); setCabName(""); setCabPos("");
        flash("✓ Запрошення відправлено на email");
        loadCabs();
      } else flash(d.error || "Помилка");
    } catch (err) { flash("Помилка: " + err.message); }
    setCabBusy(false);
  };

  const resetPass = async (cabId, email) => {
    if (!confirm(`Скинути пароль для ${email}? Старий пароль перестане працювати.`)) return;
    try {
      const r = await apiFetch(`/api/projects/${activeId}/cabinets/${cabId}/reset-password`, { method: "POST" });
      const d = await r.json();
      if (r.ok) {
        setLastPass({ email, password: d.password, url: cabUrl, reset: true });
        flash("✓ Новий пароль згенеровано (показано нижче)");
      } else flash(d.error || "Помилка");
    } catch (err) { flash("Помилка: " + err.message); }
  };

  const deactivate = async (cabId, email) => {
    if (!confirm(`Деактивувати кабінет ${email}? Клієнт втратить доступ.`)) return;
    try {
      await apiFetch(`/api/projects/${activeId}/cabinets/${cabId}`, { method: "DELETE" });
      loadCabs();
      flash("✓ Деактивовано");
    } catch (err) { flash("Помилка: " + err.message); }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(cabUrl);
    setCopied("url"); setTimeout(() => setCopied(""), 1500);
  };
  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key); setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>🔐 Кабінет клієнта — {proj.name}</h2>
      </div>

      {/* ─── Налаштування звіту: блоки + коментар + прев'ю ─── */}
      <div style={{ ...card, padding: 20, marginBottom: 14 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
          <p style={{ ...stitle, margin: 0 }}>📊 Що показувати у звіті клієнту</p>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={openPreview} disabled={previewBusy} style={btnG}>{previewBusy ? "..." : "👁 Переглянути як клієнт"}</button>
            <button onClick={saveCfg} disabled={cfgSaving} style={btnP}>{cfgSaving ? "..." : "💾 Зберегти"}</button>
          </div>
        </div>
        <p style={{ fontSize: 11, color: t.textMuted, margin: "0 0 12px" }}>Прибери зайві блоки, переставляй порядок (↑↓). Вимкнені блоки не побачить клієнт. Один набір на проєкт.</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {blocks.map((b, i) => (
            <div key={b.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, border: `1px solid ${t.border}`, background: b.enabled ? (dark ? "rgba(16,185,129,0.06)" : "rgba(16,185,129,0.04)") : "transparent", opacity: b.enabled ? 1 : 0.55 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: 1, fontSize: 13 }}>
                <input type="checkbox" checked={b.enabled} onChange={() => toggleBlock(b.key)} />
                <span style={{ fontWeight: 500 }}>{RP_BLOCK_LABELS[b.key] || b.key}</span>
              </label>
              <span style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace" }}>{i + 1}</span>
              <button onClick={() => moveBlock(i, -1)} disabled={i === 0} style={{ ...btnSm, opacity: i === 0 ? 0.3 : 1 }} title="Вище">↑</button>
              <button onClick={() => moveBlock(i, 1)} disabled={i === blocks.length - 1} style={{ ...btnSm, opacity: i === blocks.length - 1 ? 0.3 : 1 }} title="Нижче">↓</button>
            </div>
          ))}
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 6 }}>Колонки канбану у звіті <span style={{ opacity: 0.7 }}>(порожньо → стандартні «В роботі» + «Здано»)</span></label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {colOptions.length === 0 && <span style={{ fontSize: 12, color: t.textMuted }}>Колонок не знайдено</span>}
            {colOptions.map(c => {
              const on = taskColumns.includes(c.title);
              return (
                <label key={c.title} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", borderRadius: 8, border: `1px solid ${on ? "#10b981" : t.border}`, background: on ? (dark ? "rgba(16,185,129,0.1)" : "rgba(16,185,129,0.06)") : "transparent", cursor: "pointer", fontSize: 12 }}>
                  <input type="checkbox" checked={on} onChange={() => toggleCol(c.title)} />
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: c.color || "#6366f1", display: "inline-block" }} />
                  {c.title}
                </label>
              );
            })}
          </div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 6 }}>Поточний місяць плану робіт</label>
          <select value={currentMonth} onChange={e => setCurrentMonth(Number(e.target.value))}
            style={{ ...inp, width: "100%", boxSizing: "border-box" }}>
            <option value={0}>— не вказано (авто: 100%=зроблено)</option>
            {wpMonths.map((m, i) => (
              <option key={m.id || i} value={i + 1}>Місяць {m.month_no || (i + 1)} · {m.title || `Етап ${i + 1}`}</option>
            ))}
          </select>
          {currentMonth > 0 && (
            <div style={{ fontSize: 10.5, color: t.textMuted, marginTop: 4 }}>
              У звіті: місяці 1–{currentMonth - 1} → <b>ЗРОБЛЕНО</b> · місяць {currentMonth} → <b>ПОТОЧНИЙ</b> · решта → <b>ПЛАНИ</b>
            </div>
          )}
        </div>
        <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Коментар команди (показується зверху звіту)</label>
        <textarea value={reportComment} onChange={e => setReportComment(e.target.value)} rows={3}
          placeholder="Напр.: За цей місяць ми зосередились на нарощуванні позицій по комерційних запитах…"
          style={{ ...inp, width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit" }} />
      </div>

      {/* Cabinet URL */}
      {cabUrl && (
        <div style={{ ...card, padding: 16, marginBottom: 14, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Посилання на кабінет</div>
            <code style={{ fontSize: 13, color: "#6366f1", fontFamily: "monospace" }}>{cabUrl}</code>
          </div>
          <button onClick={copyUrl} style={btnG}>{copied === "url" ? "✓ Скопійовано" : "📋 Копіювати"}</button>
          <a href={cabUrl} target="_blank" rel="noopener noreferrer" style={{ ...btnG, textDecoration: "none" }}>↗ Відкрити</a>
        </div>
      )}

      {/* Last generated password */}
      {lastPass && (
        <div style={{ ...card, padding: 16, marginBottom: 14, background: dark ? "rgba(168,85,247,0.08)" : "rgba(168,85,247,0.04)", border: "1px solid rgba(168,85,247,0.3)" }}>
          <div style={{ fontSize: 13, color: t.text, marginBottom: 8 }}>
            {lastPass.reset ? "🔄 Новий пароль для" : "✅ Запрошення створено для"} <strong>{lastPass.email}</strong>
            {!lastPass.reset && <span style={{ marginLeft: 6, color: t.textMuted, fontSize: 12 }}>(відправлено на пошту)</span>}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <code style={{ fontSize: 16, fontWeight: 700, color: "#a855f7", padding: "6px 12px", background: dark ? "rgba(0,0,0,0.3)" : "#fff", borderRadius: 6, letterSpacing: 1 }}>{lastPass.password}</code>
            <button onClick={() => copyText(lastPass.password, "pass")} style={btnSm}>{copied === "pass" ? "✓" : "📋 Пароль"}</button>
            <button onClick={() => copyText(`${lastPass.email} / ${lastPass.password}\n${lastPass.url}`, "all")} style={btnSm}>{copied === "all" ? "✓" : "📋 Все"}</button>
          </div>
          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 8 }}>Запиши собі — пароль показуємо тільки один раз. Якщо клієнт не отримав листа, можна вручну переслати йому ці дані.</div>
        </div>
      )}

      {/* Invite form */}
      <div style={{ ...card, padding: 20, marginBottom: 14 }}>
        <p style={{ ...stitle, marginTop: 0 }}>Запросити клієнта</p>
        <form onSubmit={invite} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1.4, minWidth: 200 }}>
            <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Email *</label>
            <input type="email" value={cabEmail} onChange={e => setCabEmail(e.target.value)} placeholder="client@example.com" style={inp} required />
          </div>
          <div style={{ flex: 1, minWidth: 140 }}>
            <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Імʼя</label>
            <input value={cabName} onChange={e => setCabName(e.target.value)} placeholder="Іван Петренко" style={inp} />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label style={{ fontSize: 11, color: t.textMuted, display: "block", marginBottom: 4 }}>Посада</label>
            <input value={cabPos} onChange={e => setCabPos(e.target.value)} placeholder="Директор" style={inp} />
          </div>
          <button type="submit" disabled={cabBusy || !cabEmail} style={btnP}>{cabBusy ? "..." : "📤 Запросити"}</button>
        </form>
        <p style={{ fontSize: 10.5, color: t.textMuted, margin: "10px 0 0" }}>На email клієнта прийде гарне HTML-запрошення з логіном, паролем та посиланням. Якщо клієнт вже існує — пароль перезапишеться і шлеться новий лист.</p>
      </div>

      {/* List of cabinets */}
      {cabs.length > 0 ? (
        <div style={{ ...card, padding: 0 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Email</th>
                <th style={th}>Імʼя</th>
                <th style={th}>Посада</th>
                <th style={th}>Запрошено</th>
                <th style={th}>Останній вхід</th>
                <th style={th}>Входів</th>
                <th style={th}>Статус</th>
                <th style={{ ...th, width: 160 }}>Дії</th>
              </tr>
            </thead>
            <tbody>
              {cabs.map(c => (
                <tr key={c.id} style={{ opacity: c.active ? 1 : 0.5 }}>
                  <td style={td}><code style={{ color: "#6366f1" }}>{c.email}</code></td>
                  <td style={td}>{c.full_name || "—"}</td>
                  <td style={{ ...td, fontSize: 11, color: t.textMuted }}>{c.position || "—"}</td>
                  <td style={{ ...td, fontSize: 11, color: t.textMuted }}>{new Date(c.invited_at).toLocaleDateString("uk-UA")}</td>
                  <td style={{ ...td, fontSize: 11, color: t.textMuted }}>{c.last_login_at ? new Date(c.last_login_at).toLocaleDateString("uk-UA") : "—"}</td>
                  <td style={{ ...td, fontSize: 11, fontFamily: "monospace" }}>{c.login_count || 0}</td>
                  <td style={td}>
                    <span style={{ fontSize: 11, padding: "2px 6px", borderRadius: 5, background: c.active ? "rgba(16,185,129,0.15)" : "rgba(239,68,68,0.15)", color: c.active ? "#10b981" : "#ef4444", fontWeight: 600 }}>
                      {c.active ? "Активний" : "Деактивовано"}
                    </span>
                  </td>
                  <td style={td}>
                    {c.active && (
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => resetPass(c.id, c.email)} style={btnSm} title="Згенерувати новий пароль">🔄</button>
                        <button onClick={() => deactivate(c.id, c.email)} style={{ ...btnSm, color: "#ef4444" }} title="Деактивувати">×</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ textAlign: "center", color: t.textMuted, fontSize: 13, marginTop: 24 }}>Ще немає кабінетів. Запросіть першого клієнта вище.</p>
      )}
    </div>
  );
}
